import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Map, {
  Layer,
  Marker,
  NavigationControl,
  Source,
  type MapRef,
} from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import type { StyleSpecification } from 'maplibre-gl';
import {
  CircleDot,
  LineChart,
  Lock,
  LogIn,
  LogOut,
  Pencil,
  Plus,
  Shapes,
  Trash2,
  Undo2,
  X,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { geofencesApi } from '@/api/endpoints/geofences';
import { devicesApi } from '@/api/endpoints/devices';
import { permissionsApi } from '@/api/endpoints/permissions';
import { ApiError } from '@/api/client';
import { useFleetStore } from '@/stores/fleet';
import {
  areaToGeoJSON,
  formatWkt,
  parseWkt,
  type GeofenceShape,
  type ParsedArea,
} from '@/lib/wkt';
import { cn } from '@/lib/utils';
import type { Geofence } from '@/api/types';
import { useRef } from 'react';

const STREETS_STYLE: StyleSpecification = {
  version: 8,
  sources: {
    'carto-voyager': {
      type: 'raster',
      tiles: [
        'https://a.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}@2x.png',
        'https://b.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}@2x.png',
        'https://c.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}@2x.png',
        'https://d.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}@2x.png',
      ],
      tileSize: 256,
      attribution:
        '© <a href="https://carto.com/attributions">CARTO</a> · © <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    },
  },
  layers: [{ id: 'carto-voyager', type: 'raster', source: 'carto-voyager' }],
};

const DEFAULT_COLOR = '#0A84FF';

interface GeofenceAttrs {
  color: string;
  blockOnEnter: boolean;
  blockOnExit: boolean;
}

function readAttrs(g: Geofence): GeofenceAttrs {
  const a = g.attributes ?? {};
  return {
    color: typeof a.color === 'string' ? (a.color as string) : DEFAULT_COLOR,
    blockOnEnter: a.blockOnEnter === true || a.blockOnEnter === 'true',
    blockOnExit: a.blockOnExit === true || a.blockOnExit === 'true',
  };
}

export function GeofencesPage() {
  const list = useQuery({
    queryKey: ['geofences'],
    queryFn: () => geofencesApi.list(),
  });

  const [editing, setEditing] = useState<Geofence | 'new' | null>(null);

  return (
    <div className="space-y-4 p-4 sm:p-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">Geocercas</h1>
          <p className="text-sm text-muted-foreground">
            Áreas de monitoramento. Use círculo, polígono ou linha.
          </p>
        </div>
        <Button onClick={() => setEditing('new')} className="gap-1.5">
          <Plus className="h-4 w-4" />
          Nova
        </Button>
      </div>

      {list.isLoading ? (
        <p className="p-6 text-sm text-muted-foreground">Carregando geocercas…</p>
      ) : list.error ? (
        <p className="p-6 text-sm text-destructive">Erro ao carregar geocercas.</p>
      ) : !list.data || list.data.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 p-10 text-center">
            <Shapes className="h-10 w-10 text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">
              Nenhuma geocerca cadastrada. Crie a primeira clicando em <strong>Nova</strong>.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {list.data.map((g) => (
            <GeofenceCard
              key={g.id}
              geofence={g}
              onEdit={() => setEditing(g)}
            />
          ))}
        </div>
      )}

      {editing && (
        <GeofenceEditor
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function GeofenceCard({ geofence, onEdit }: { geofence: Geofence; onEdit: () => void }) {
  const area = useMemo(() => parseWkt(geofence.area), [geofence.area]);
  const attrs = readAttrs(geofence);
  const Icon = area?.type === 'CIRCLE' ? CircleDot : area?.type === 'LINESTRING' ? LineChart : Shapes;

  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <span
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full"
              style={{ background: `${attrs.color}1A`, color: attrs.color }}
            >
              <Icon className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm font-bold">{geofence.name}</p>
              {geofence.description && (
                <p className="truncate text-xs text-muted-foreground">{geofence.description}</p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onEdit}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-accent"
            aria-label="Editar"
          >
            <Pencil className="h-4 w-4" />
          </button>
        </div>

        <div className="flex flex-wrap gap-1.5 text-[11px]">
          <span className="rounded-full bg-secondary px-2 py-0.5 font-semibold">
            {area?.type === 'CIRCLE'
              ? `Círculo · ${Math.round(area.radiusMeters)}m`
              : area?.type === 'POLYGON'
                ? `Polígono · ${area.ring.length} pts`
                : area?.type === 'LINESTRING'
                  ? `Linha · ${area.line.length} pts`
                  : 'Formato desconhecido'}
          </span>
          {attrs.blockOnEnter && (
            <span className="inline-flex items-center gap-1 rounded-full bg-status-blocked/10 px-2 py-0.5 font-semibold text-status-blocked">
              <LogIn className="h-3 w-3" />
              Bloquear ao entrar
            </span>
          )}
          {attrs.blockOnExit && (
            <span className="inline-flex items-center gap-1 rounded-full bg-status-blocked/10 px-2 py-0.5 font-semibold text-status-blocked">
              <LogOut className="h-3 w-3" />
              Bloquear ao sair
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function GeofenceEditor({
  initial,
  onClose,
}: {
  initial: Geofence | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const initialArea = initial ? parseWkt(initial.area) : null;
  const initialAttrs = initial ? readAttrs(initial) : { color: DEFAULT_COLOR, blockOnEnter: false, blockOnExit: false };

  const allDevices = useFleetStore((s) => s.devices);
  const sortedDevices = useMemo(
    () => Array.from(allDevices.values()).sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')),
    [allDevices],
  );

  // Carrega quais máquinas já estão vinculadas à geocerca (modo edição)
  const linkedDevicesQuery = useQuery({
    queryKey: ['geofence-linked-devices', initial?.id],
    queryFn: async () => {
      if (!initial?.id) return [] as number[];
      const list = await devicesApi.list({ /* uses query param */ } as never);
      // Traccar não tem deviceId filter por geofenceId direto; usamos /devices?geofenceId=X
      const filtered = await fetch(`/api/devices?geofenceId=${initial.id}`, { credentials: 'include' });
      if (filtered.ok) {
        const data = (await filtered.json()) as { id: number }[];
        return data.map((d) => d.id);
      }
      return list.map((d) => d.id).filter(Boolean);
    },
    enabled: Boolean(initial?.id),
  });

  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [color, setColor] = useState(initialAttrs.color);
  const [blockOnEnter, setBlockOnEnter] = useState(initialAttrs.blockOnEnter);
  const [blockOnExit, setBlockOnExit] = useState(initialAttrs.blockOnExit);
  const [linkedDeviceIds, setLinkedDeviceIds] = useState<Set<number>>(new Set());
  const [deviceSearch, setDeviceSearch] = useState('');

  // Sincroniza linkedDeviceIds quando vem do servidor
  useEffect(() => {
    if (linkedDevicesQuery.data) {
      setLinkedDeviceIds(new Set(linkedDevicesQuery.data));
    }
  }, [linkedDevicesQuery.data]);

  const [shape, setShape] = useState<GeofenceShape>(initialArea?.type ?? 'CIRCLE');
  const [center, setCenter] = useState<[number, number] | null>(
    initialArea?.type === 'CIRCLE' ? initialArea.center : null,
  );
  const [radius, setRadius] = useState<number>(
    initialArea?.type === 'CIRCLE' ? initialArea.radiusMeters : 200,
  );
  const [points, setPoints] = useState<[number, number][]>(
    initialArea?.type === 'POLYGON'
      ? initialArea.ring.slice(0, -1)
      : initialArea?.type === 'LINESTRING'
        ? initialArea.line
        : [],
  );

  const mapRef = useRef<MapRef | null>(null);

  // Reseta a geometria ao trocar o tipo (mas mantém quando carregando edição inicial)
  useEffect(() => {
    if (initialArea && shape === initialArea.type) return;
    setCenter(null);
    setPoints([]);
  }, [shape, initialArea]);

  const handleMapClick = (e: { lngLat: { lng: number; lat: number } }) => {
    const point: [number, number] = [e.lngLat.lng, e.lngLat.lat];
    if (shape === 'CIRCLE') {
      setCenter(point);
    } else {
      setPoints((prev) => [...prev, point]);
    }
  };

  const undoLast = () => {
    if (shape === 'CIRCLE') setCenter(null);
    else setPoints((prev) => prev.slice(0, -1));
  };

  const buildArea = (): ParsedArea | null => {
    if (shape === 'CIRCLE') {
      if (!center) return null;
      return { type: 'CIRCLE', center, radiusMeters: radius };
    }
    if (shape === 'POLYGON') {
      if (points.length < 3) return null;
      return { type: 'POLYGON', ring: points };
    }
    if (points.length < 2) return null;
    return { type: 'LINESTRING', line: points };
  };

  const previewArea = useMemo(() => buildArea(), [shape, center, radius, points]);
  const previewGeoJson = useMemo(
    () => (previewArea ? areaToGeoJSON(previewArea) : null),
    [previewArea],
  );

  const initialView = useMemo(() => {
    if (initialArea?.type === 'CIRCLE') {
      return { longitude: initialArea.center[0], latitude: initialArea.center[1], zoom: 13 };
    }
    if (initialArea?.type === 'POLYGON') {
      return { longitude: initialArea.ring[0][0], latitude: initialArea.ring[0][1], zoom: 13 };
    }
    if (initialArea?.type === 'LINESTRING') {
      return { longitude: initialArea.line[0][0], latitude: initialArea.line[0][1], zoom: 13 };
    }
    return { longitude: -47, latitude: -15, zoom: 4 };
  }, [initialArea]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const area = buildArea();
      if (!area) throw new Error('Geometria incompleta');
      const wkt = formatWkt(area);
      const body: Geofence = {
        ...(initial ?? { id: 0, name: '', area: '' }),
        name: name.trim(),
        description: description.trim() || undefined,
        area: wkt,
        attributes: {
          ...(initial?.attributes ?? {}),
          color,
          blockOnEnter,
          blockOnExit,
        },
      } as Geofence;
      const saved = initial
        ? await geofencesApi.update(initial.id, body)
        : await (async () => {
            const { id, ...rest } = body;
            void id;
            return geofencesApi.create(rest);
          })();

      // Sincroniza vínculos com máquinas
      const geofenceId = (saved as Geofence | undefined)?.id ?? initial?.id;
      if (geofenceId) {
        const previouslyLinked = new Set(linkedDevicesQuery.data ?? []);
        const target = linkedDeviceIds;
        // Adicionar novos
        for (const id of target) {
          if (!previouslyLinked.has(id)) {
            try {
              await permissionsApi.link({ geofenceId, deviceId: id });
            } catch (e) {
              console.warn('[geofence-link] erro ao vincular', id, e);
            }
          }
        }
        // Remover desmarcados
        for (const id of previouslyLinked) {
          if (!target.has(id)) {
            try {
              await permissionsApi.unlink({ geofenceId, deviceId: id });
            } catch (e) {
              console.warn('[geofence-unlink] erro ao desvincular', id, e);
            }
          }
        }
      }
      return saved;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['geofences'] });
      await queryClient.invalidateQueries({ queryKey: ['geofence-linked-devices'] });
      onClose();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => geofencesApi.delete(initial!.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['geofences'] });
      onClose();
    },
  });

  const canSave = name.trim().length > 0 && previewArea !== null;

  const errorText = (e: unknown) => {
    if (e instanceof ApiError) return `Servidor respondeu ${e.status} (${e.statusText || 'erro'}).`;
    if (e instanceof Error) return e.message;
    return 'Erro desconhecido.';
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/60 p-2 sm:p-6"
      onClick={onClose}
    >
      <div
        className="flex max-h-[95vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl bg-card shadow-xl ring-1 ring-border"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-border px-5 py-3">
          <h2 className="text-base font-bold">
            {initial ? 'Editar geocerca' : 'Nova geocerca'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:bg-accent"
            aria-label="Fechar"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[360px_1fr]">
          <aside className="space-y-3 overflow-y-auto border-b border-border p-4 lg:border-b-0 lg:border-r">
            <Field label="Nome" required>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </Field>
            <Field label="Descrição">
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Field>

            <Field label="Formato">
              <div className="flex gap-1">
                <ShapeButton
                  active={shape === 'CIRCLE'}
                  onClick={() => setShape('CIRCLE')}
                  icon={CircleDot}
                  label="Círculo"
                />
                <ShapeButton
                  active={shape === 'POLYGON'}
                  onClick={() => setShape('POLYGON')}
                  icon={Shapes}
                  label="Polígono"
                />
                <ShapeButton
                  active={shape === 'LINESTRING'}
                  onClick={() => setShape('LINESTRING')}
                  icon={LineChart}
                  label="Linha"
                />
              </div>
            </Field>

            {shape === 'CIRCLE' && (
              <Field label="Raio (metros)">
                <Input
                  type="number"
                  min={10}
                  step={10}
                  value={radius}
                  onChange={(e) => setRadius(Math.max(10, Number(e.target.value) || 0))}
                />
              </Field>
            )}

            <Field label="Cor">
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="h-10 w-12 cursor-pointer rounded border border-input bg-background"
                />
                <Input
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="font-mono"
                />
              </div>
            </Field>

            <div className="space-y-2 rounded-md border border-border p-3">
              <p className="text-xs font-semibold text-muted-foreground">
                Ações automáticas
              </p>
              <Toggle
                icon={LogIn}
                label="Bloquear ao entrar"
                checked={blockOnEnter}
                onChange={setBlockOnEnter}
              />
              <Toggle
                icon={LogOut}
                label="Bloquear ao sair"
                checked={blockOnExit}
                onChange={setBlockOnExit}
              />
              <p className="text-[10px] text-muted-foreground">
                Disparam RELAY,1# automaticamente quando a máquina entra/sai e
                está vinculada à geocerca. Disparo é feito pelo frontend ao
                receber o evento via WebSocket.
              </p>
            </div>

            <div className="space-y-2 rounded-md border border-border p-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-muted-foreground">
                  Máquinas vinculadas
                </p>
                <span className="text-[10px] font-semibold text-primary">
                  {linkedDeviceIds.size} selecionadas
                </span>
              </div>
              <Input
                value={deviceSearch}
                onChange={(e) => setDeviceSearch(e.target.value)}
                placeholder="Buscar máquina"
                className="h-8 text-xs"
              />
              <div className="max-h-40 overflow-y-auto rounded border border-border bg-card">
                {sortedDevices
                  .filter((d) =>
                    deviceSearch.trim()
                      ? d.name.toLowerCase().includes(deviceSearch.toLowerCase()) ||
                        d.uniqueId.toLowerCase().includes(deviceSearch.toLowerCase())
                      : true,
                  )
                  .map((d) => (
                    <label
                      key={d.id}
                      className="flex cursor-pointer items-center gap-2 px-2 py-1.5 text-xs hover:bg-accent"
                    >
                      <input
                        type="checkbox"
                        checked={linkedDeviceIds.has(d.id)}
                        onChange={(e) => {
                          setLinkedDeviceIds((prev) => {
                            const next = new Set(prev);
                            if (e.target.checked) next.add(d.id);
                            else next.delete(d.id);
                            return next;
                          });
                        }}
                        className="h-3.5 w-3.5 accent-primary"
                      />
                      <span className="truncate">{d.name}</span>
                      {d.model && (
                        <span className="truncate text-muted-foreground">· {d.model}</span>
                      )}
                    </label>
                  ))}
                {sortedDevices.length === 0 && (
                  <p className="p-3 text-xs text-muted-foreground">
                    Nenhuma máquina carregada.
                  </p>
                )}
              </div>
              <p className="text-[10px] text-muted-foreground">
                Só máquinas vinculadas disparam eventos geofenceEnter/Exit.
              </p>
            </div>

            <div className="flex flex-wrap gap-2 pt-2">
              <Button
                size="sm"
                variant="outline"
                onClick={undoLast}
                disabled={
                  shape === 'CIRCLE' ? center === null : points.length === 0
                }
                className="gap-1.5"
              >
                <Undo2 className="h-3.5 w-3.5" />
                Desfazer ponto
              </Button>
              {initial && (
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => deleteMutation.mutate()}
                  disabled={deleteMutation.isPending}
                  className="ml-auto gap-1.5"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  {deleteMutation.isPending ? 'Excluindo…' : 'Excluir'}
                </Button>
              )}
            </div>

            {(saveMutation.error || deleteMutation.error) && (
              <p className="rounded-md bg-status-blocked/10 px-3 py-2 text-xs font-medium text-status-blocked">
                {errorText(saveMutation.error ?? deleteMutation.error)}
              </p>
            )}
          </aside>

          <div className="relative min-h-[300px] lg:min-h-0">
            <div className="absolute left-3 top-3 z-10 rounded-full bg-card/95 px-3 py-1.5 text-[11px] font-medium shadow ring-1 ring-border">
              {shape === 'CIRCLE'
                ? center
                  ? 'Centro definido. Ajuste o raio à esquerda.'
                  : 'Clique no mapa pra definir o centro do círculo.'
                : shape === 'POLYGON'
                  ? `Clique no mapa pra adicionar pontos (${points.length}).`
                  : `Clique no mapa pra adicionar pontos da linha (${points.length}).`}
            </div>

            <Map
              ref={mapRef}
              initialViewState={initialView}
              mapStyle={STREETS_STYLE}
              style={{ width: '100%', height: '100%' }}
              attributionControl={false}
              onClick={handleMapClick}
            >
              <NavigationControl position="top-right" showCompass={false} />

              {previewGeoJson && (
                <Source id="preview" type="geojson" data={previewGeoJson}>
                  {(previewArea?.type === 'CIRCLE' || previewArea?.type === 'POLYGON') && (
                    <>
                      <Layer
                        id="preview-fill"
                        type="fill"
                        paint={{ 'fill-color': color, 'fill-opacity': 0.18 }}
                      />
                      <Layer
                        id="preview-line"
                        type="line"
                        paint={{ 'line-color': color, 'line-width': 2 }}
                      />
                    </>
                  )}
                  {previewArea?.type === 'LINESTRING' && (
                    <Layer
                      id="preview-line"
                      type="line"
                      paint={{ 'line-color': color, 'line-width': 3 }}
                    />
                  )}
                </Source>
              )}

              {shape === 'CIRCLE' && center && (
                <Marker longitude={center[0]} latitude={center[1]}>
                  <span
                    className="block h-3 w-3 rounded-full border-2 border-white shadow"
                    style={{ background: color }}
                  />
                </Marker>
              )}
              {shape !== 'CIRCLE' &&
                points.map((p, i) => (
                  <Marker key={i} longitude={p[0]} latitude={p[1]}>
                    <span
                      className="block h-2.5 w-2.5 rounded-full border-2 border-white shadow"
                      style={{ background: color }}
                    />
                  </Marker>
                ))}
            </Map>
          </div>
        </div>

        <footer className="flex items-center justify-between gap-3 border-t border-border bg-card px-5 py-3">
          <p className="text-xs text-muted-foreground">
            {previewArea === null
              ? 'Geometria incompleta'
              : 'Pré-visualização atualizada no mapa'}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              onClick={() => saveMutation.mutate()}
              disabled={!canSave || saveMutation.isPending}
              className="gap-1.5"
            >
              <Lock className="h-4 w-4" />
              {saveMutation.isPending
                ? 'Salvando…'
                : initial
                  ? 'Salvar alterações'
                  : 'Criar geocerca'}
            </Button>
          </div>
        </footer>
      </div>
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="space-y-1 block">
      <span className="text-xs font-semibold text-muted-foreground">
        {label}
        {required && <span className="text-status-blocked"> *</span>}
      </span>
      {children}
    </label>
  );
}

function ShapeButton({
  active,
  onClick,
  icon: Icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: typeof CircleDot;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex flex-1 flex-col items-center gap-1 rounded-md border px-2 py-2 text-[11px] font-semibold transition-colors',
        active
          ? 'border-primary bg-primary/10 text-primary'
          : 'border-border text-muted-foreground hover:bg-accent',
      )}
    >
      <Icon className="h-4 w-4" />
      {label}
    </button>
  );
}

function Toggle({
  icon: Icon,
  label,
  checked,
  onChange,
}: {
  icon: typeof LogIn;
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2 text-sm">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 cursor-pointer rounded border-border accent-primary"
      />
      <Icon className="h-4 w-4 text-muted-foreground" />
      <span className="font-medium">{label}</span>
    </label>
  );
}
